/**
 * The one rule for phone numbers on their way into the database: no spaces.
 *
 * People type `+351 912 345 678`, `912 345 678` and `+351912345678` for the
 * same number, and every one of those spellings used to be stored verbatim.
 * That matters more than tidiness because `patients` is deduplicated by the
 * partial unique indexes `patients_clinic_email_uq` / `patients_clinic_phone_uq`
 * — plain equality on the stored text. Two spellings of one number are two
 * patients, two consent states, and two sets of reminders for one human.
 *
 * This is deliberately CONSERVATIVE: whitespace (and the invisible characters
 * that paste in alongside it from Word, WhatsApp and PDFs) is removed and
 * nothing else. `+`, `(`, `-` and the leading zeros of an international prefix
 * all survive, because they carry meaning we cannot recover once dropped and
 * because a stored number is also a number staff read back to a patient.
 *
 * For MATCHING across spellings that survived — the last-9-digits rule used to
 * tie a person to their consent subject — see `normalizePhone` in
 * `src/server/consent/subjects.ts`. That one is lossy on purpose and must never
 * be what gets stored.
 */

/**
 * Whitespace plus the zero-width characters that paste in looking like none:
 * ZWSP/ZWNJ/ZWJ (U+200B–U+200D), word joiner (U+2060) and BOM (U+FEFF). `\s`
 * already covers NBSP and the narrow no-break space Word likes to insert.
 */
const BLANKS = /[\s\u200B-\u200D\u2060\uFEFF]+/g;

/**
 * The one client-side shape check every public form applies to its phone
 * field (contact, ads lead, booking): lenient on spacing and punctuation,
 * strict enough to refuse a clearly-not-a-number entry. Test against the
 * TRIMMED value. Server-side truth stays with the zod schemas.
 */
export const PHONE_RE = /^\+?[\d\s()-]{9,18}$/;

/** Strip every space from a phone number. Returns `''` for a blank input. */
export function compactPhone(value: string): string {
  return value.replace(BLANKS, '');
}

/**
 * Storage form of an optional phone: compacted, or NULL when nothing is left.
 * Use this at every write to `patients.phone`, `leads.phone` and
 * `contact_messages.phone`.
 */
export function compactPhoneOrNull(value: string | null | undefined): string | null {
  const compacted = compactPhone(String(value ?? ''));
  return compacted.length > 0 ? compacted : null;
}
